import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import projectsData from '../projects_dataset.json';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { sendEmail, buildVisitorEmail, buildOwnerEmail, OWNER_EMAIL } from '../services/emailService';

// ─── Groq AI Config ────────────────────────────────────────────────────────────
const GROQ_API_KEY = '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';
const GROQ_MODEL   = 'groq/compound-mini';
const GROQ_URL     = 'https://api.groq.com/openai/v1/chat/completions';

const CATEGORIES = projectsData.metadata.categories;
const LEVELS     = ['BE/B.Tech', 'M.Tech', 'PhD', 'Diploma', 'MCA', 'MBA', 'BSc'];

// ─── Get random projects from dataset by category+level ───────────────────────
function getProjectSuggestions(category, level, count = 5) {
  const all = projectsData.projects.filter(
    (p) =>
      p.category === category &&
      (level ? p.level === level : true)
  );
  const shuffled = all.sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

// ─── Groq AI call ──────────────────────────────────────────────────────────────
async function generateGroqReport(projectTitle, techStack, category, level, description) {
  const prompt = `You are a senior academic project advisor at Connect Students, India's top academic project company.

A student has requested a project report for:
- Project Title: "${projectTitle}"
- Category: ${category}
- Academic Level: ${level}
- Tech Stack: ${Array.isArray(techStack) ? techStack.join(', ') : techStack}
- Description: ${description || 'Not provided'}

Generate a concise but detailed project proposal report with these sections:

## 1. Project Overview
(2-3 sentences on what this project does and its real-world impact)

## 2. How It Works
(Step-by-step working in 4-5 bullet points — simple, student-friendly language)

## 3. System Architecture
(Describe the modules: Input → Processing → Output. List 3-4 key modules)

## 4. Technology Stack
(List technologies with their specific roles in the project)

## 5. 15-Day Delivery Timeline
Day 1-3: [phase]
Day 4-7: [phase]
Day 8-11: [phase]
Day 12-14: [phase]
Day 15: [phase]

## 6. Deliverables Included
(List 6 items: code, report, PPT, demo video, research paper guidance, viva support)

## 7. Research Paper Guidance
(Which journals to target: IEEE/Springer/Elsevier. Key algorithms and novelty points to highlight)

## 8. Why This Project Stands Out
(2-3 compelling reasons for evaluators and industry)

Keep the tone professional yet student-friendly. Be specific with technologies and numbers.`;

  const res = await fetch(GROQ_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${GROQ_API_KEY}`,
    },
    body: JSON.stringify({
      model: GROQ_MODEL,
      messages: [{ role: 'user', content: prompt }],
      temperature: 0.7,
      max_tokens: 1200,
    }),
  });

  if (!res.ok) throw new Error('Groq API error');
  const data = await res.json();
  return data.choices[0].message.content;
}

// ─── Render markdown-ish text ──────────────────────────────────────────────────
function RenderText({ text }) {
  const lines = text.split('\n');
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        if (line.startsWith('## ')) {
          return (
            <p key={i} className="font-playfair font-bold text-royal-navy text-sm mt-3 mb-1 border-b border-royal-gold/30 pb-1">
              {line.replace('## ', '')}
            </p>
          );
        }
        if (line.startsWith('**') && line.endsWith('**')) {
          return <p key={i} className="font-inter font-bold text-royal-navy text-xs">{line.replace(/\*\*/g, '')}</p>;
        }
        const bold = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        return (
          <p key={i} className="font-inter text-xs text-slate-royal/80 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: bold || '&nbsp;' }} />
        );
      })}
    </div>
  );
}

// ─── FLOW STATES ───────────────────────────────────────────────────────────────
// idle → ask_idea → (has_idea: pick_category → pick_level → pick_project → collect_info)
//                   (no_idea:  pick_category → pick_level → suggest_projects → confirm_project → collect_info)
// → generating → report_done

const BOT_AVATAR = (
  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
    style={{ background: 'linear-gradient(135deg, #C9A84C, #F0D080)' }}>
    <svg className="w-3.5 h-3.5 text-royal-navy" viewBox="0 0 24 24" fill="currentColor">
      <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"/>
    </svg>
  </div>
);

export default function ChatBot() {
  const [open, setOpen]         = useState(false);
  const [unread, setUnread]     = useState(1);
  const [flow, setFlow]         = useState('idle');           // current flow state
  const [messages, setMessages] = useState([]);
  const [typing, setTyping]     = useState(false);
  const [input, setInput]       = useState('');

  // flow data
  const [hasIdea, setHasIdea]           = useState(null);
  const [selCategory, setSelCategory]   = useState('');
  const [selLevel, setSelLevel]         = useState('');
  const [selProject, setSelProject]     = useState(null);
  const [suggestions, setSuggestions]   = useState([]);

  // user details
  const [userInfo, setUserInfo]         = useState({ name:'', phone:'', email:'', college:'', description:'' });
  const [infoStep, setInfoStep]         = useState(0); // which field we're collecting

  // report
  const [report, setReport]     = useState('');
  const [reportError, setReportError] = useState('');

  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  const INFO_FIELDS = [
    { key:'name',        label:'Full Name',                    placeholder:'e.g. Rahul Sharma' },
    { key:'phone',       label:'Phone / WhatsApp Number',      placeholder:'e.g. +91 98765 43210' },
    { key:'email',       label:'Email Address',                placeholder:'e.g. rahul@gmail.com' },
    { key:'college',     label:'College / Organisation Name',  placeholder:'e.g. VIT Vellore' },
    { key:'description', label:'Brief Project Description (optional)', placeholder:'Any specific requirements...' },
  ];

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages, typing]);
  useEffect(() => { if (open) { setUnread(0); setTimeout(() => inputRef.current?.focus(), 300); } }, [open]);

  // ── helpers ────────────────────────────────────────────────────────────────
  const pushBot = (text, extra = {}) => {
    setMessages(prev => [...prev, { id: Date.now() + Math.random(), from:'bot', text, time: new Date(), ...extra }]);
  };
  const pushUser = (text) => {
    setMessages(prev => [...prev, { id: Date.now(), from:'user', text, time: new Date() }]);
  };
  const delayBot = (text, ms = 800, extra = {}) => {
    setTyping(true);
    setTimeout(() => { setTyping(false); pushBot(text, extra); }, ms);
  };

  // ── Initial greeting when chat opens for first time ────────────────────────
  useEffect(() => {
    if (open && messages.length === 0) {
      pushBot(`Hello! 👋 Welcome to **Connect Students AI Assistant**.\n\nI'm here to help you find the perfect academic project, suggest ideas, and generate a detailed project report — all powered by AI!\n\nWhat would you like to do?`, { choices: ['Talk to Expert — Get My Project', 'Browse Projects by Domain', 'Know Pricing & Delivery'] });
    }
  }, [open]);

  // ── Handle choice buttons ───────────────────────────────────────────────────
  const handleChoice = (choice) => {
    pushUser(choice);

    if (choice === 'Talk to Expert — Get My Project' || choice === 'Browse Projects by Domain') {
      setFlow('ask_idea');
      delayBot(`Great! Let's find the perfect project for you.\n\nDo you already have a **project idea or topic** in mind?`, 900, {
        choices: ['Yes, I have an idea', "No, please suggest me a project"],
      });

    } else if (choice === 'Know Pricing & Delivery') {
      delayBot(`Our pricing is affordable and transparent:\n\n**Diploma / BSc** — Starting ₹2,999\n**BE / BTech** — Starting ₹4,999\n**MTech / ME** — Starting ₹7,999\n**MCA / MBA** — Starting ₹5,999\n**PhD Research** — Starting ₹14,999\n\n✅ All packages include:\n• Source code + documentation\n• PPT + Research paper\n• Demo video\n• Viva support\n• **Delivery within 15 days**\n\nWould you like to get started?`, 1000, {
        choices: ['Yes, find me a project', 'Talk to Expert'],
      });

    } else if (choice === 'Yes, find me a project' || choice === 'Talk to Expert') {
      setFlow('ask_idea');
      delayBot(`Do you already have a **project idea or topic** in mind?`, 700, {
        choices: ['Yes, I have an idea', "No, please suggest me a project"],
      });

    } else if (choice === 'Yes, I have an idea') {
      setHasIdea(true);
      setFlow('pick_category');
      delayBot(`Which **domain / category** does your project belong to?\n\nSelect the closest match:`, 800, { showCategories: true });

    } else if (choice === "No, please suggest me a project") {
      setHasIdea(false);
      setFlow('pick_category');
      delayBot(`No worries — we have **10,800+ project ideas** in our database! 🎉\n\nFirst, which **domain** are you interested in?`, 900, { showCategories: true });

    } else if (choice.startsWith('CAT:')) {
      const cat = choice.replace('CAT:', '');
      setSelCategory(cat);
      setFlow('pick_level');
      delayBot(`Great choice! **${cat}**\n\nNow, what is your **academic level**?`, 700, { showLevels: true });

    } else if (choice.startsWith('LVL:')) {
      const lvl = choice.replace('LVL:', '');
      setSelLevel(lvl);

      if (hasIdea) {
        // has idea — ask them to type it
        setFlow('has_idea_input');
        delayBot(`Perfect! Tell me your **project idea or title**.\n\nType your idea below and I'll generate a full AI project report for you!`, 800);
      } else {
        // no idea — suggest from dataset
        const sugg = getProjectSuggestions(selCategory, lvl, 5);
        setSuggestions(sugg);
        setFlow('suggest_projects');
        delayBot(`Here are **5 handpicked project ideas** for you from our database!\n\nSelect one and I'll generate a complete AI project report:`, 1000, { showSuggestions: sugg });
      }

    } else if (choice.startsWith('PROJ:')) {
      const projId = parseInt(choice.replace('PROJ:', ''));
      const proj = suggestions.find(p => p.id === projId) || { title: choice.replace('PROJ:', ''), suggested_tech_stack: [], category: selCategory, level: selLevel };
      setSelProject(proj);
      setFlow('collect_info');
      setInfoStep(0);
      delayBot(`Excellent choice! 🎯\n\n**"${proj.title}"**\n\nBefore I generate your AI project report, I need a few quick details.\n\nWhat is your **Full Name**?`, 800);

    } else if (choice === 'Generate My Project Report') {
      startGeneratingReport();
    } else if (choice === 'Start Over') {
      resetChat();
    } else if (choice === 'WhatsApp Expert') {
      window.open('https://wa.me/917676654809', '_blank');
    }
  };

  // ── Text input handling ─────────────────────────────────────────────────────
  const handleSend = (text) => {
    const val = text || input.trim();
    if (!val) return;
    setInput('');
    pushUser(val);

    if (flow === 'has_idea_input') {
      // user typed their project idea
      const proj = {
        id: 0,
        title: val,
        category: selCategory,
        level: selLevel,
        suggested_tech_stack: [],
        description: val,
      };
      setSelProject(proj);
      setFlow('collect_info');
      setInfoStep(0);
      delayBot(`Fantastic idea! I'll generate a full report for:\n\n**"${val}"**\n\nFirst, what is your **Full Name**?`, 900);
      return;
    }

    if (flow === 'collect_info') {
      const field = INFO_FIELDS[infoStep];
      
      if (field.key === 'email' && (!val.includes('@') || !val.includes('.'))) {
        pushBot("Please provide a valid email address (e.g., name@gmail.com).");
        return;
      }
      
      setUserInfo(prev => ({ ...prev, [field.key]: val }));
      const nextStep = infoStep + 1;

      if (nextStep < INFO_FIELDS.length) {
        setInfoStep(nextStep);
        const nextField = INFO_FIELDS[nextStep];
        const isOptional = nextField.key === 'description';
        delayBot(`Got it! ${nextStep === 1 ? '📱' : nextStep === 2 ? '📧' : nextStep === 3 ? '🏫' : '📝'} Now, please share your **${nextField.label}**${isOptional ? ' (or type "skip")' : ''}:`, 700);
      } else {
        // all info collected
        setFlow('confirm');
        const info = { ...userInfo, [field.key]: val };
        setUserInfo(info);
        delayBot(`Thank you, **${info.name || userInfo.name}**! ✅\n\nHere's what I have:\n📋 **Project:** ${selProject?.title}\n🎓 **Level:** ${selLevel}\n📁 **Domain:** ${selCategory}\n👤 **Name:** ${info.name || userInfo.name}\n📱 **Phone:** ${info.phone || userInfo.phone}\n📧 **Email:** ${info.email || userInfo.email}\n🏫 **College:** ${info.college || userInfo.college}\n\nReady to generate your **AI Project Report**?`, 1000, {
          choices: ['Generate My Project Report', 'Start Over'],
        });
      }
      return;
    }

    // fallback
    delayBot(`I'm here to help you find a project! Let's get started.`, 700, {
      choices: ['Talk to Expert — Get My Project', 'Browse Projects by Domain'],
    });
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  // ── Generate Report ─────────────────────────────────────────────────────────
  const startGeneratingReport = async () => {
    setFlow('generating');
    setTyping(true);
    pushBot(`🤖 **Generating your AI Project Report...**\n\nThis may take 10–20 seconds. Our AI is crafting:\n• Project overview & architecture\n• Tech stack breakdown\n• 15-day timeline\n• Research paper guidance`, { isGenerating: true });

    let generatedReport = '';
    let aiFailed = false;

    try {
      generatedReport = await generateGroqReport(
        selProject?.title,
        selProject?.suggested_tech_stack,
        selCategory,
        selLevel,
        userInfo.description || selProject?.description
      );
      setReport(generatedReport);
      setFlow('report_done');
      setTyping(false);
      setMessages(prev => [...prev, {
        id: Date.now(),
        from: 'bot',
        text: generatedReport,
        time: new Date(),
        isReport: true,
        choices: ['WhatsApp Expert', 'Start Over'],
      }]);
    } catch (err) {
      aiFailed = true;
      setTyping(false);
      setReportError('Failed to generate report. Please try again.');
      setFlow('idle');
      pushBot(`Sorry, there was an issue generating the report. Please try again or WhatsApp our expert directly!`, {
        choices: ['WhatsApp Expert', 'Start Over'],
      });
    }

    try {
      await addDoc(collection(db, 'connect_students'), {
        name: userInfo.name,
        phone: userInfo.phone,
        email: userInfo.email,
        college: userInfo.college,
        description: userInfo.description || selProject?.description || '',
        category: selCategory,
        level: selLevel,
        projectTitle: selProject?.title || '',
        report: generatedReport || 'AI Generation Failed',
        timestamp: new Date().toISOString()
      });
    } catch (e) {
      console.error('Firebase save error:', e);
    }

    // Send Email to Visitor
    try {
      await sendEmail({
        to: userInfo.email,
        subject: "Your AI Project Report - Connect Students",
        text: buildVisitorEmail({
          name: userInfo.name || 'Student',
          email: userInfo.email,
          projectTitle: selProject?.title || 'Your Project',
          report: generatedReport || 'Our AI is currently experiencing high demand and could not generate the full report at this moment. However, we have received your project details and our academic expert will reach out to you shortly to provide the complete architecture, timeline, and deliverables.',
          domain: selCategory || '',
          level: selLevel || ''
        })
      });
    } catch (e) {
      console.error('Visitor email send error:', e);
    }

    // Send Email to Owner
    try {
      await sendEmail({
        to: OWNER_EMAIL,
        subject: `New Lead (ChatBot): ${selProject?.title || 'Project'} - Connect Students`,
        text: buildOwnerEmail({
          name: userInfo.name || 'N/A',
          email: userInfo.email || 'N/A',
          phone: userInfo.phone || 'N/A',
          college: userInfo.college || 'N/A',
          description: userInfo.description || selProject?.description || '',
          projectTitle: selProject?.title || 'N/A',
          domain: selCategory || 'N/A',
          level: selLevel || 'N/A',
          report: generatedReport || 'AI Generation Failed. Lead captured via ChatBot.'
        })
      });
    } catch (e) {
      console.error('Owner email send error:', e);
    }
  };

  const resetChat = () => {
    setFlow('idle');
    setHasIdea(null);
    setSelCategory('');
    setSelLevel('');
    setSelProject(null);
    setSuggestions([]);
    setUserInfo({ name:'', phone:'', email:'', college:'', description:'' });
    setInfoStep(0);
    setReport('');
    setMessages([]);
    setTimeout(() => {
      pushBot(`Hello again! 😊 How can I help you?\n\nWhat would you like to do?`, {
        choices: ['Talk to Expert — Get My Project', 'Browse Projects by Domain', 'Know Pricing & Delivery'],
      });
    }, 400);
  };

  const formatTime = (d) => d.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' });

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Chat Window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: 30 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-32px)] sm:w-[400px] max-w-[400px] rounded-3xl overflow-hidden shadow-2xl border border-white/20 flex flex-col"
            style={{ height: 'min(600px, calc(100vh - 120px))' }}
          >
            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #0B1C4E 0%, #1E3A6E 100%)' }}>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #C9A84C, #F0D080)' }}>
                    <svg className="w-5 h-5 text-royal-navy" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"/>
                    </svg>
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-400 border-2 border-royal-navy" />
                </div>
                <div>
                  <p className="font-inter font-bold text-white text-sm">CS Expert AI</p>
                  <p className="font-inter text-xs text-green-400">Online • Powered by Groq AI</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" style={{ background: '#FAFAF5' }}>
              {messages.map((msg) => (
                <motion.div key={msg.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>

                  {msg.from === 'bot' && BOT_AVATAR}

                  <div className={`max-w-[88%] space-y-2`}>
                    {/* Bubble */}
                    <div
                      className={`rounded-2xl px-4 py-3 ${
                        msg.from === 'user'
                          ? 'text-white rounded-tr-sm text-xs'
                          : msg.isReport
                            ? 'bg-white border-2 border-royal-gold/30 rounded-tl-sm shadow-md'
                            : 'bg-white border border-parchment-dark rounded-tl-sm'
                      }`}
                      style={msg.from === 'user' ? { background: 'linear-gradient(135deg, #0B1C4E, #1E3A6E)' } : {}}
                    >
                      {msg.isReport ? (
                        <div>
                          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-royal-gold/20">
                            <span className="text-lg">📋</span>
                            <div>
                              <p className="font-playfair font-bold text-royal-navy text-sm">AI Project Report</p>
                              <p className="font-inter text-xs text-royal-gold">Generated by Compound Mini</p>
                            </div>
                          </div>
                          <RenderText text={msg.text} />
                        </div>
                      ) : (
                        <RenderText text={msg.text} />
                      )}
                    </div>

                    {/* Choice buttons */}
                    {msg.choices && (
                      <div className="flex flex-col gap-1.5">
                        {msg.choices.map((c) => (
                          <button key={c} onClick={() => handleChoice(c)}
                            className="text-left text-xs font-inter font-semibold text-royal-navy border-2 border-royal-gold/40 bg-white hover:bg-royal-gold/10 hover:border-royal-gold px-4 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-royal-gold flex-shrink-0" />
                            {c}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Category grid */}
                    {msg.showCategories && (
                      <div className="grid grid-cols-1 gap-1.5 max-h-48 overflow-y-auto pr-1">
                        {CATEGORIES.map((cat) => (
                          <button key={cat} onClick={() => handleChoice(`CAT:${cat}`)}
                            className="text-left text-xs font-inter font-medium text-royal-navy bg-white border border-parchment-dark hover:border-royal-gold hover:bg-royal-gold/5 px-3 py-2 rounded-xl transition-all duration-200">
                            {cat}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Level buttons */}
                    {msg.showLevels && (
                      <div className="flex flex-wrap gap-1.5">
                        {LEVELS.map((lvl) => (
                          <button key={lvl} onClick={() => handleChoice(`LVL:${lvl}`)}
                            className="text-xs font-inter font-bold text-royal-navy border-2 border-royal-gold/40 hover:bg-royal-gold/15 hover:border-royal-gold px-3 py-1.5 rounded-full transition-all duration-200 bg-white">
                            {lvl}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Project suggestions */}
                    {msg.showSuggestions && (
                      <div className="space-y-2">
                        {msg.showSuggestions.map((proj, i) => (
                          <button key={proj.id} onClick={() => handleChoice(`PROJ:${proj.id}`)}
                            className="w-full text-left bg-white border border-parchment-dark hover:border-royal-gold hover:shadow-gold rounded-xl p-3 transition-all duration-200 group">
                            <div className="flex items-start gap-2">
                              <span className="w-5 h-5 rounded-full bg-royal-gold/15 text-royal-gold text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                              <div>
                                <p className="font-inter font-semibold text-royal-navy text-xs leading-snug group-hover:text-royal-gold transition-colors">{proj.title}</p>
                                <div className="flex flex-wrap gap-1 mt-1.5">
                                  {proj.suggested_tech_stack.slice(0, 3).map((t) => (
                                    <span key={t} className="bg-royal-navy/5 text-royal-navy text-[10px] px-1.5 py-0.5 rounded-full font-medium">{t}</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}

                    <p className={`text-[10px] text-slate-royal/40 ${msg.from === 'user' ? 'text-right' : 'text-left'}`}>
                      {formatTime(msg.time)}
                    </p>
                  </div>
                </motion.div>
              ))}

              {/* Typing indicator */}
              <AnimatePresence>
                {typing && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    className="flex items-center gap-2">
                    {BOT_AVATAR}
                    <div className="bg-white border border-parchment-dark rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1 items-center">
                      {[0, 1, 2].map((i) => (
                        <motion.div key={i} className="w-1.5 h-1.5 rounded-full bg-royal-gold"
                          animate={{ y: [0, -5, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }} />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <div ref={bottomRef} />
            </div>

            {/* Input — shown only when text input is expected */}
            {(flow === 'has_idea_input' || flow === 'collect_info') && (
              <div className="px-4 py-3 bg-white border-t border-parchment-dark flex items-center gap-2 flex-shrink-0">
                <input ref={inputRef} type={flow === 'collect_info' && INFO_FIELDS[infoStep]?.key === 'email' ? 'email' : 'text'}
                  value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKey}
                  placeholder={flow === 'has_idea_input' ? 'Type your project idea...' : INFO_FIELDS[infoStep]?.placeholder || 'Type your answer...'}
                  className="flex-1 text-xs font-inter bg-parchment border border-parchment-dark rounded-full px-4 py-2.5 focus:outline-none focus:border-royal-gold focus:ring-1 focus:ring-royal-gold/20 transition-all" />
                <motion.button onClick={() => handleSend()} whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.93 }}
                  disabled={!input.trim()}
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 disabled:opacity-40 transition-all"
                  style={{ background: 'linear-gradient(135deg, #0B1C4E, #1E3A6E)' }}>
                  <svg className="w-4 h-4 text-white translate-x-0.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                  </svg>
                </motion.button>
              </div>
            )}

            {/* Progress indicator */}
            {flow === 'collect_info' && (
              <div className="px-4 py-2 bg-white border-t border-parchment-dark flex-shrink-0">
                <div className="flex items-center gap-1">
                  {INFO_FIELDS.map((_, i) => (
                    <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${i <= infoStep ? 'bg-royal-gold' : 'bg-parchment-dark'}`} />
                  ))}
                </div>
                <p className="font-inter text-[10px] text-slate-royal/50 mt-1">Step {infoStep + 1} of {INFO_FIELDS.length}</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Trigger Button */}
      <motion.button
        id="chatbot-trigger"
        onClick={() => { setOpen(o => !o); setUnread(0); }}
        whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.93 }}
        className="fixed bottom-5 right-5 sm:right-6 z-50 w-16 h-16 rounded-full shadow-2xl flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0B1C4E 0%, #1E3A6E 100%)', boxShadow: '0 8px 32px rgba(11,28,78,0.4), 0 0 0 4px rgba(201,168,76,0.15)' }}
        animate={!open ? { boxShadow: ['0 8px 32px rgba(11,28,78,0.4), 0 0 0 4px rgba(201,168,76,0.15)', '0 8px 32px rgba(11,28,78,0.4), 0 0 0 9px rgba(201,168,76,0.07)', '0 8px 32px rgba(11,28,78,0.4), 0 0 0 4px rgba(201,168,76,0.15)'] } : {}}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.svg key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}
              className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </motion.svg>
          ) : (
            <motion.div key="bot" initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="relative">
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"/>
              </svg>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Unread badge */}
        <AnimatePresence>
          {unread > 0 && !open && (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 border-2 border-white flex items-center justify-center">
              <span className="text-white text-[10px] font-bold">{unread}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Tooltip */}
      <AnimatePresence>
        {!open && (
          <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }} transition={{ delay: 2 }}
            className="fixed bottom-7 right-24 z-50 hidden sm:block pointer-events-none">
            <div className="bg-royal-navy text-white text-xs font-inter font-semibold px-3 py-2 rounded-full shadow-lg whitespace-nowrap">
              Get Free Project Report
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1 w-2 h-2 bg-royal-navy rotate-45" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
